import axios from "axios";
import { showErrorNotification, showSuccessNotification } from './notificationService';

// Configuration constants
const BASE_URLS = {
  production: "https://api.tanaka.co.za/api/",
  staging: "https://sapi.tanaka.co.za/api/",
  development: "http://localhost:4000/api/"
};

// Environment detection function
const getEnvironment = () => {
  if (typeof window !== 'undefined') {
    const url = window.location.href;
    if (url.includes('localhost')) {
      return 'development';
    } else if (url.includes('staging')) {
      return 'staging';
    }
  }
  return 'production';
};

const DEFAULT_TIMEOUT = 30000; // 30 seconds
const MAX_RETRIES = 0;
const RETRY_DELAY = 1000;
const RETRYABLE_STATUS_CODES = [408, 429, 500, 502, 503, 504];

// Global state for request management
let refreshPromise = null;
const pendingRequests = new Set();
const inflightRequests = new Map(); // Deduplicates concurrent GET requests to same URL

// Axios instance with default timeout
const apiClient = axios.create({
  timeout: DEFAULT_TIMEOUT,
  validateStatus: (status) => (status >= 200 && status < 300) || status === 501
});

// Main API request function
export const apiRequest = async (config) => {
  const source = axios.CancelToken.source();
  const extendedConfig = {
    ...config,
    cancelToken: source.token,
    timeout: config.timeout || DEFAULT_TIMEOUT,
    responseType: config.responseType || 'json' // Support blob responses
  };

  pendingRequests.add(source);

  try {
    const response = await withRetry(() => makeRequest(extendedConfig));

    // Show success notification for successful POST/PUT requests (skip for blobs)
    if (['post', 'put'].includes(config.method?.toLowerCase()) && [200, 201].includes(response.status) && !config.responseType) {
      const successMessage = response.data?.message || 'Operation completed successfully';
      if (!config.suppressNotifications) {
        showSuccessNotification('Success', successMessage);
      }
    }

    return response;
  } catch (error) {
    if (!axios.isCancel(error)) {
      handleApiError(error, extendedConfig); // Pass extendedConfig instead of config
    }
    throw error;
  } finally {
    pendingRequests.delete(source);
  }
};

// Enhanced error handler with user-friendly messages
// Enhanced error handler with user-friendly messages
const handleApiError = (error, config) => {
  // Skip notification for canceled requests
  if (axios.isCancel(error)) return;

  // Skip notification if explicitly disabled in config
  if (config.suppressNotifications) return;

  if (error.response) {
    const response = error.response;
    const status = response.status;
    const errorData = response.data || {};

    // Extract error message - check both 'message' and 'error' fields
    let errorMessage = errorData.message || errorData.error || 'Request failed';
    
    // Include error code if available and no message exists
    if (!errorData.message && errorData.code) {
      errorMessage = `${errorData.code}: ${errorMessage}`;
    }

    // Handle specific error types with user-friendly messages
    if (errorData.code === 'UNIQUE_CONSTRAINT') {
      const field = errorData.details?.field || '';
      let message = 'This item already exists';
      
      if (field.includes('email')) {
        message = 'Email address already exists';
      } else if (field.includes('phone')) {
        message = 'Phone number already exists';
      } else if (field.includes('username')) {
        message = 'Username already taken';
      }
      
      showErrorNotification(message, 'Please use a different value');
      return;
    }

    // Handle other error types
    switch (status) {
      case 400:
        if (errorData.code === 'VALIDATION_ERROR') {
          showErrorNotification('Validation Error', errorMessage);
        } else {
          showErrorNotification('Bad Request', errorMessage);
        }
        break;
      case 401:
        // Only show notification for login failures
        if (config.url.includes('login') || config.url.includes('security')) {
          showErrorNotification('Login Failed', errorMessage || 'Invalid credentials');
        }
        // Don't show notification for other 401 errors (handled by token refresh)
        break;
      case 403:
        showErrorNotification('Forbidden', errorMessage || 'You don\'t have permission to access this resource');
        break;
      case 404:
        showErrorNotification('Not Found', errorMessage || 'The requested resource was not found');
        break;
      case 409:
        showErrorNotification('Conflict', errorMessage || 'This action conflicts with existing data');
        break;
      case 422:
        showErrorNotification('Validation Error', errorMessage);
        break;
      case 429:
        showErrorNotification('Too Many Requests', errorMessage || 'Please wait before making more requests');
        break;
      case 500:
        if (errorData.code === 'DATABASE_ERROR') {
          showErrorNotification('Database Error', errorMessage || 'Something went wrong with the database operation');
        } else {
          showErrorNotification('Server Error', errorMessage || 'Something went wrong on our server. Please try again later');
        }
        break;
      default:
        showErrorNotification(`Error ${status}`, errorMessage);
    }
  } else if (error.request) {
    // Request was made but no response received
    if (error.code === 'ECONNABORTED') {
      showErrorNotification('Request Timeout', 'The request took too long. Please try again');
    } else {
      showErrorNotification('Network Error', 'Please check your internet connection and try again');
    }
  } else {
    // Something happened in setting up the request
    showErrorNotification('Request Error', error.message);
  }
};

// Request execution with token handling and deduplication
const makeRequest = async (config) => {
  const method = (config.method || 'get').toLowerCase();
  const environment = getEnvironment();
  const baseUrl = BASE_URLS[config.endpointType] || BASE_URLS[environment];

  // Deduplicate concurrent GET requests to the same URL
  // This prevents duplicate calls (e.g. two wallet calls on page load) from
  // overwhelming the server and hitting connection limits
  if (method === 'get') {
    const requestKey = `${baseUrl}${config.url}`;
    if (inflightRequests.has(requestKey)) {
      return inflightRequests.get(requestKey);
    }

    const promise = executeRequest(config, baseUrl);
    inflightRequests.set(requestKey, promise);
    promise.finally(() => inflightRequests.delete(requestKey));
    return promise;
  }

  return executeRequest(config, baseUrl);
};

// Actual request execution (separated for dedup logic above)
const executeRequest = async (config, baseUrl) => {
  const token = localStorage.getItem("accessToken");

  const requestConfig = {
    ...config,
    baseURL: baseUrl,
    headers: {
      ...config.headers,
      ...(token && { Authorization: `Bearer ${token}` })
    }
  };

  try {
    return await apiClient(requestConfig);
  } catch (error) {
    // Check if it's a 401 error and not a login/refresh/security request
    if (error.response?.status === 401 &&
        !config.url.includes('login') &&
        !config.url.includes('refresh') && !config.url.includes('security')) {

      // While an admin is operating as a client there is deliberately no refresh
      // token for the impersonation session. Refreshing here would fall back to the
      // ADMIN's stashed refresh token and silently replay this request with admin
      // identity — a write meant for the customer executing as an admin. Instead,
      // end the impersonation and restore the admin session.
      if (localStorage.getItem("impersonation")) {
        endImpersonationLocally();
        throw error;
      }

      try {
        // Use deduplicated token refresh (prevents multiple concurrent refresh calls)
        const refreshData = await handleTokenRefresh();

        // Retry original request with new token
        return await apiClient({
          ...requestConfig,
          headers: {
            ...requestConfig.headers,
            Authorization: `Bearer ${refreshData.accessToken}`
          }
        });
      } catch (refreshError) {
        handleLogout();
        throw refreshError;
      }
    }

    throw error;
  }
};

// Token refresh handler with queuing
const handleTokenRefresh = async () => {
  if (refreshPromise) return refreshPromise;

  try {
    refreshPromise = (async () => {
      const refreshToken = localStorage.getItem("refreshToken");
      if (!refreshToken) throw new Error("No refresh token available");

      const response = await apiClient.post(
        'api/refresh',
        { refreshToken },
        { timeout: DEFAULT_TIMEOUT }
      );

      if (!response.data.accessToken) {
        throw new Error("Invalid refresh token response");
      }

      localStorage.setItem("accessToken", response.data.accessToken);
      // Only update refresh token if new one was provided
      if (response.data.refreshToken) {
        localStorage.setItem("refreshToken", response.data.refreshToken);
      }
      return response.data;
    })();

    return await refreshPromise;
  } catch (error) {
    // Don't show notification here - it will be handled by the calling function
    throw error;
  } finally {
    refreshPromise = null;
  }
};

// Retry logic with exponential backoff
const withRetry = async (fn, attempt = 0) => {
  try {
    return await fn();
  } catch (error) {
    if (attempt >= MAX_RETRIES || !isRetryable(error)) throw error;

    await new Promise(resolve => setTimeout(
      resolve, 
      RETRY_DELAY * Math.pow(2, attempt)
    ));

    return withRetry(fn, attempt + 1);
  }
};

// Helper functions
const isNetworkError = (error) => (
  !error.response && 
  (error.code === 'ECONNABORTED' || error.message.includes('Network Error'))
);

const isRetryable = (error) => (
  isNetworkError(error) ||
  RETRYABLE_STATUS_CODES.includes(error.response?.status)
);

// Logout and cleanup
export const handleLogout = () => {
  localStorage.clear();
  cancelAllRequests();
  window.location.href = "/login";
};

/**
 * Restore the admin session when an impersonation token dies mid-flight.
 *
 * Deliberately does NOT clear localStorage like handleLogout: the admin is still
 * legitimately signed in, they were merely acting as a customer. Kept here (rather
 * than in AuthContext) because the 401 handler in this module runs outside React.
 */
export const endImpersonationLocally = () => {
  const adminToken = localStorage.getItem("admin_accessToken");
  const adminRefresh = localStorage.getItem("admin_refreshToken");
  const adminUser = localStorage.getItem("admin_user");

  localStorage.removeItem("impersonation");
  localStorage.removeItem("admin_accessToken");
  localStorage.removeItem("admin_refreshToken");
  localStorage.removeItem("admin_user");

  if (adminToken) {
    localStorage.setItem("accessToken", adminToken);
    if (adminRefresh) localStorage.setItem("refreshToken", adminRefresh);
    if (adminUser) localStorage.setItem("user", adminUser);
    window.location.href = "/admin/users";
    return;
  }
  handleLogout();
};

export const cancelAllRequests = (message = 'Request canceled') => {
  pendingRequests.forEach(source => source.cancel(message));
  pendingRequests.clear();
};
